import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DataTable from 'shared/components/table/DataTable';
import { Title } from 'shared/components/abstract/interactables';
import { searchTickets } from 'shared/api/support';
import { withDateEditorFilter, withSelectorSwitch } from 'shared/components/data-display/editors';
import { defaultSearch } from 'shared/utils';

import {
  buildingEntity,
  complexEntity,
  employeeEntity,
  nasEntity,
  ticketTagEntity, unitEntity,
} from 'shared/entityDetails';
import { FilterRedirect } from 'shared/hoc/withFilters';
import {
  withAsyncSelect,
  withDependentMultipleSelect,
  withMultipleSelect,
  withStaticSelect,
} from 'shared/components/data-display/withSelectors';
import { ticketIconColumns } from 'shared/components/table/ticketColumns';
import { TICKET_PRIORITY_OPTIONS, TICKET_STATUS_OPTIONS } from '../../shared/constants/support';
import { FILTER_DISPLAY_TYPE } from '../../shared/constants';

const buildingColumn = {
  name: 'Building',
  jsonKey: 'building',
  formatter: (row) => (row.ticket_association?.building
    && <div className="small-line-clamp">{row.ticket_association.building.name}</div>),
};

ticketIconColumns.splice(3, 0, buildingColumn);

const filters = [
  {
    label: 'Status',
    param: 'status',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withStaticSelect({
      options: TICKET_STATUS_OPTIONS,
    }),
  },
  {
    label: 'Priority',
    param: 'priority',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withStaticSelect({
      options: TICKET_PRIORITY_OPTIONS,
    }),
  },
  {
    label: 'Tag',
    param: 'tag_id',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withMultipleSelect(ticketTagEntity),
    entity: ticketTagEntity,
  },
  {
    label: 'Assignee',
    param: 'assignee_id',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withAsyncSelect(employeeEntity),
    entity: employeeEntity,
  },
  {
    label: 'Building',
    param: 'building_id',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withAsyncSelect(buildingEntity),
    entity: buildingEntity,
  },
  {
    label: 'Unit',
    param: 'unit_id',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withDependentMultipleSelect({ ...unitEntity, dependentKey: 'building_id' }),
    entity: { ...unitEntity, dependentKey: 'building_id' },
  },
  {
    label: 'Complex',
    param: 'complex_id',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withAsyncSelect(complexEntity),
    entity: complexEntity,
  },
  {
    label: 'Nas',
    param: 'nas_id',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withAsyncSelect(nasEntity),
    entity: nasEntity,
  },
  {
    label: 'Created After Date',
    param: 'start_date',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withDateEditorFilter(),
  },
  {
    label: 'Created Before Date',
    param: 'end_date',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withDateEditorFilter(),
  },
  {
    label: 'Search Ticket Entries',
    param: 'ticket_entry_search',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withSelectorSwitch(),
  },
];

/**
 * @param {Object} props
 * @param {string} props.savedFilters
 * @param {string} props.filterKey
 * @param {string} props.path
 */
export default function TicketSearch({
  filterKey,
  savedFilters,
  path,
}) {
  const { search } = useLocation();
  const [placeHolder, setPlaceHolder] = useState('Enter to search tickets');
  useEffect(() => {
    const entrySearch = new URLSearchParams(search).get('ticket_entry_search');
    if (entrySearch) {
      setPlaceHolder('Enter to search ticket entries');
    } else {
      setPlaceHolder('Enter to search tickets');
    }
  }, [search]);

  const baseSearch = useMemo(() => defaultSearch(
    search,
    { with_association: 'True' },
  ), [search]);

  if (!FilterRedirect(path, savedFilters)) {
    return (
      <>
        <Title title="Tickets" />
        <DataTable
          columns={ticketIconColumns}
          filters={filters}
          baseSearch={baseSearch}
          filterKey={filterKey}
          searchRequest={searchTickets}
          className="table-height-large"
          rowId="id"
          placeholder={placeHolder}
        />
      </>
    );
  }
}
